/**
 * Shared pricing configuration.
 * Single source of truth — consumed by SubscriptionOverview and SubscriptionContext.
 * PaymentBilling reads order totals via context (no local pricing data).
 */

export const PLAN_PRICING = {
  monthly: {
    baseFee: 89.99,
    maxSeats: 2,
    label: "Monthly Plan — Without EHR",
    shortLabel: "Without EHR",
    description: "Core platform access for solo practitioners and small teams",
    includesEhr: false,
    cycle: "monthly",
    features: [
      "Up to 2 provider seats",
      "Core platform access",
      "HIPAA-compliant video calls",
      "Role-based access control",
    ],
  },
  monthlyWithEhr: {
    baseFee: 129.99,
    maxSeats: 2,
    label: "Monthly Plan — With EHR",
    shortLabel: "With EHR",
    description: "Everything in the base plan plus Athena EHR integration",
    includesEhr: true,
    cycle: "monthly",
    features: [
      "Up to 2 provider seats",
      "Everything in Without EHR",
      "Athena EHR Integration",
      "Bi-directional EHR sync",
    ],
  },
  annual: {
    baseFee: 74.99,
    maxSeats: 2,
    label: "Annual Plan — Without EHR",
    shortLabel: "Without EHR",
    description: "Core platform access for solo practitioners and small teams",
    includesEhr: false,
    cycle: "annual",
    features: [
      "Up to 2 provider seats",
      "Core platform access",
      "HIPAA-compliant video calls",
      "Role-based access control",
    ],
  },
  annualWithEhr: {
    baseFee: 109.99,
    maxSeats: 2,
    label: "Annual Plan — With EHR",
    shortLabel: "With EHR",
    description: "Everything in the base plan plus Athena EHR integration",
    includesEhr: true,
    cycle: "annual",
    features: [
      "Up to 2 provider seats",
      "Everything in Without EHR",
      "Athena EHR Integration",
      "Bi-directional EHR sync",
    ],
  },
};

export const PLAN_KEYS = Object.keys(PLAN_PRICING);

export const PROCESSING_FEE_RATE = 0.029;

/**
 * Compute a full order summary from plan selection.
 * Returns { lineItems, subtotal, processingFee, total, planLabel, seatCount, includesEhr }
 */
export function computeOrderSummary({ planType, seatCount } = {}) {
  const pricing = PLAN_PRICING[planType] || PLAN_PRICING.monthly;
  const seats = seatCount || 1;

  const lineItems = [
    { label: pricing.label, amount: pricing.baseFee },
  ];

  const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
  const processingFee = Math.round(subtotal * PROCESSING_FEE_RATE * 100) / 100;
  lineItems.push({ label: "Processing Fee (2.9%)", amount: processingFee });

  const total = subtotal + processingFee;

  return {
    lineItems,
    subtotal,
    processingFee,
    total,
    planLabel: pricing.label,
    seatCount: seats,
    includesEhr: pricing.includesEhr,
  };
}
